/**
 * 이상한 방 - 확률 띠·바늘·범례 + 피티 게이지 (순수 DOM)
 *
 * 근거 문서: 로드맵 확정안 3.3절(300~1100ms - 바늘이 실제 폭의 띠를 훑는다) · 3.6절(확률은 표 하나에서 화면으로)
 *           4절 8·9번(하드 피티 · 정직한 바늘) · 6절("근접 실패 색 깜빡" 기각 - 바늘은 실제 값)
 *
 * `ui/envelope.ts`에서 갈라져 나왔다. 이 화면에서 **거짓말이 가능한 자리**가 전부 여기 모여 있다 - 띠의 폭,
 * 바늘이 멎는 자리, 확률 문자열, 피티 남은 장수. 그래서 이 파일의 규칙은 하나다: **값을 만들지 않는다.**
 * 띠의 폭은 `gradeBands(table)`, 바늘은 `gradeRoll` 실수 그대로(`toFixed(4)`는 인라인 스타일의 표기일 뿐이다),
 * 확률 문자열은 `gradeOddsLabel`, 남은 장수는 `pityRemaining`이다. 여기서 `'60%'`를 적거나 바늘을 경계 옆으로
 * 밀면 화면이 확률표와 다른 말을 하고, 확률 표시 의무가 있는 지역에서는 그것이 곧 위반이다.
 *
 * 언제 바늘이 출발하는가는 시각표(`envelopeTimeline.ts`)와 그것을 돌리는 지휘자(`envelopePhases.ts`)가 정한다. 여기는 목적지를 심을 뿐이고, 감속은 CSS 전환 곡선의 몫이다.
 *
 * ## 릴도 여기 산다 - 같은 띠를 여러 번 이어 붙인 것뿐이다
 *
 * 확률 띠 위에 **돌아가는 릴**이 하나 더 앉는다. 트랙은 `gradeBands(table)`를 `run.copies`번 이어 붙인 것이라
 * 폭이 여전히 실제 확률이고, 마커 아래에 멎는 자리는 `gradeRoll` 그 값이다. 산술(바퀴 수·위치·똑딱 시각)은
 * 전부 `envelopeReel.ts`의 순수 함수에서 오고 이 파일은 **노드를 짓고 변수를 심을 뿐이다** - 여기서 위치를
 * 계산하기 시작하면 "값을 만들지 않는다"가 깨진다.
 *
 * 릴이 돌아도 아래의 띠·바늘·범례·확률 문자열은 그대로 남는다. 릴은 극이고 띠는 영수증이다 -
 * 확률 표시 의무는 띠가 지고, 릴은 그 띠를 흐르게 할 뿐이라 새로 말하는 것이 하나도 없다.
 *
 * 값을 만들지 않는다는 규칙의 유일한 예외처럼 보이는 자리가 `spinCarry`인데, 그것도 값을 짓는 것이 아니라
 * **지금 화면에 보이는 자리를 읽어 그대로 옮겨 적는 것**이다(제자리 회전 → 감속의 이음매. `reelCarry` 주석).
 *
 * ## 릴에 얹은 셋은 전부 등급을 받지 않는다
 *
 * 브라우저에서 보고 나서 얹은 것이 셋이다 - 사본 위의 **눈금**(평평한 N 띠 위에서도 속도가 읽히게),
 * 창 양 끝의 **그늘**(눈을 마커로 모은다), 멎는 순간의 **빛 한 번**(`landReel`). 셋 다 인자가 없거나
 * 등급을 받지 않고, 네 세그먼트 위에 똑같이 덮이며, 폭을 하나도 건드리지 않는다 - "값을 만들지 않는다"는
 * 이 파일의 규칙이 그림에도 그대로 적용된 것이다.
 */

import { gradeBands, pityRemaining, type Pity } from '../game/economy/envelope'
import { GRADES, gradeNameKey, gradeOddsLabel, type EnvelopeTable, type Grade } from '../game/economy/envelopeTable'
import { t } from '../i18n'

import { createElement, setBarRatio, setText } from './dom'
import { REEL, REEL_EASE_CSS, REEL_GRAIN_CSS, reelCarry, reelRunFor, reelShift } from './envelopeReel'

import './envelope.css'
import './envelopeReel.css'

/* --- 타입 ---------------------------------------------------------------------- */

export type EnvelopeGauge = Readonly<{
  /** `.ev-gauge` - 띠·바늘·범례. */
  root: HTMLElement
  /** `.ev-pity` - 피티 두 줄. 따로 두는 이유는 출발점을 모를 때 이것만 통째로 감추기 때문이다. */
  pityRoot: HTMLElement
  /** 띠의 칸을 표의 가중치로 다시 짓는다. 표가 바뀌는 자리(`?table=` 류)마다 부른다. */
  paintBand: (table: EnvelopeTable) => void
  /** 범례의 등급 이름·확률 문자열. 언어 전환도 이 함수를 다시 부른다. */
  paintLegend: (table: EnvelopeTable) => void
  /** 바늘을 왼쪽 끝으로. 전환도 다시 켠다. */
  resetNeedle: () => void
  /** 바늘의 목적지. `instant`면 전환 없이 그 자리에 선다(탭 · 움직임 줄이기). */
  setNeedle: (gradeRoll: number, instant: boolean) => void
  /**
   * 이번 회차의 릴을 짓고 출발 자리에 세운다. **출발 자리는 상수라 네 등급이 여기서 완전히 같다**
   * (`envelopeReel.ts` 머리말). 표가 바뀌는 자리·회차가 바뀌는 자리마다 다시 부른다.
   */
  armReel: (table: EnvelopeTable, gradeRoll: number, seq: number) => void
  /** 열 장에서 앞의 아홉이 흐르는 동안의 제자리 회전. 한 바퀴의 길이(ms)만 받는다. 0이면 돌지 않는다. */
  leadReel: (periodMs: number) => void
  /** 감속을 시작한다. `durationMs` 뒤에 `gradeRoll` 자리에 멎는다. */
  spinReel: (durationMs: number) => void
  /**
   * 릴이 멎는 순간의 표식 한 번. **등급을 받지 않는다** - 언제나 같은 시각(`sweepEndMs`)에 같은 빛이라
   * 무엇이 나왔는지 말하지 않고 "여기서 멎었다"만 말한다. 정지 구간이 밋밋하다는 판단에서 나온 물건이다.
   */
  landReel: () => void
  /** 전환 없이 멎은 자리로(탭 · 움직임 줄이기). */
  settleReel: () => void
  /** 피티 두 줄. `null`이면 게이지를 통째로 감춘다(출발점 없는 도착점은 거짓이다). */
  paintPity: (value: Pity | null, table: EnvelopeTable) => void
}>

type PityRow = Readonly<{ label: HTMLElement; fill: HTMLElement }>

/* --- DOM 헬퍼 ------------------------------------------------------------------ */

/** 피티 한 줄. 라벨 + 채워지는 막대. 값은 `paintPity`가 채운다. */
function createPityRow(parent: HTMLElement): PityRow {
  const row = createElement('div', 'ev-pity__row')
  const label = createElement('span', 'ev-pity__label', '')
  const track = createElement('span', 'ev-pity__track')
  track.setAttribute('aria-hidden', 'true')
  const fill = createElement('span', 'ev-pity__fill')
  track.append(fill)
  row.append(label, track)
  parent.append(row)
  return { label, fill }
}

/**
 * 확률 띠 한 벌의 칸들. **띠와 릴이 같은 함수를 쓴다** - 폭을 두 곳에서 계산하면 둘 중 하나가
 * 언젠가 표와 다른 말을 하고, 그때 화면에는 서로 다른 확률이 둘 뜬다.
 */
function appendBand(parent: HTMLElement, table: EnvelopeTable, className: string): void {
  for (const segment of gradeBands(table)) {
    const node = createElement('span', className)
    node.dataset.grade = segment.grade
    node.style.flexBasis = `${((segment.to - segment.from) * 100).toFixed(3)}%`
    parent.append(node)
  }
}

/* --- 본체 ---------------------------------------------------------------------- */

/** 게이지와 피티를 만든다. 붙이지 않는다 - 본문 안 어디에 앉는지는 화면이 정한다. */
export function createEnvelopeGauge(): EnvelopeGauge {
  const gauge = createElement('section', 'ev-gauge')

  /*
   * 릴. 창(`.ev-reel`) 안에서 `.ev-reel__spin`이 제자리 회전(열 장의 리드)을, `.ev-reel__track`이 감속을 맡는다.
   * 둘로 나눈 이유는 하나의 요소에 `animation`과 `transition`을 같은 속성으로 걸 수 없어서다 -
   * 겹치면 리드가 끝나는 순간 트랙이 튄다.
   */
  const reel = createElement('div', 'ev-reel')
  reel.setAttribute('aria-hidden', 'true')
  const reelSpin = createElement('div', 'ev-reel__spin')
  const reelTrack = createElement('div', 'ev-reel__track')
  reelSpin.append(reelTrack)
  reel.append(reelSpin, createElement('div', 'ev-reel__marker'))

  /* 띠와 바늘은 한 겹 안에 산다 - 바늘의 `top`이 띠를 기준으로 잡혀야 릴이 위에 앉아도 자리가 안 흔들린다. */
  const strip = createElement('div', 'ev-gauge__strip')
  const band = createElement('div', 'ev-gauge__band')
  band.setAttribute('aria-hidden', 'true')
  const needle = createElement('div', 'ev-gauge__needle')
  needle.setAttribute('aria-hidden', 'true')
  strip.append(band, needle)

  const legend = createElement('ul', 'ev-gauge__legend')
  const legendItems = new Map<Grade, HTMLElement>()
  for (const grade of GRADES) {
    const item = createElement('li', 'ev-gauge__odds')
    item.dataset.grade = grade
    legend.append(item)
    legendItems.set(grade, item)
  }
  gauge.append(reel, strip, legend)
  gauge.style.setProperty('--ev-reel-ease', REEL_EASE_CSS)
  gauge.style.setProperty('--ev-reel-unit', `${(100 / REEL.perWindow).toFixed(3)}%`)
  gauge.style.setProperty('--ev-reel-grain', REEL_GRAIN_CSS)

  /** 이번 회차의 릴. `armReel`이 앉히기 전에는 돌 것이 없으므로 착지 자리도 출발 자리다. */
  let run = reelRunFor(0, 0)
  /** 지금 걸린 리드 한 바퀴의 길이(ms). 감속으로 넘어갈 때 잔여 위상을 재는 데만 쓴다. */
  let leadPeriodMs = 0

  /** 트랙을 그 자리에 놓는다. 위치 계산은 `reelShift` 하나가 하고 여기서는 심기만 한다. */
  function placeTrack(pos: number): void {
    reelTrack.style.setProperty('--ev-reel-shift', reelShift(pos, run.copies).toFixed(5))
  }

  /**
   * 제자리 회전이 지금 보여 주고 있는 잔여 위상(사본 단위). 값을 만드는 것은 `reelCarry`이고 여기서는
   * 애니메이션에게 시각만 물어본다 - `getAnimations`가 없는 환경(jsdom)에서는 0이라 지금까지와 같다.
   */
  function spinCarry(): number {
    const elapsed = reelSpin.getAnimations?.()[0]?.currentTime
    return typeof elapsed === 'number' ? reelCarry(elapsed, leadPeriodMs) : 0
  }

  const pity = createElement('section', 'ev-pity')
  const pityRare = createPityRow(pity)
  const pityLegend = createPityRow(pity)

  return {
    root: gauge,
    pityRoot: pity,

    /** 띠의 실제 폭. 표에서 온 가중치 그대로라 60 : 30 : 9 : 1이다. */
    paintBand(table: EnvelopeTable): void {
      band.replaceChildren()
      appendBand(band, table, 'ev-gauge__seg')
    },

    paintLegend(table: EnvelopeTable): void {
      legend.setAttribute('aria-label', t('envelope.oddsTitle'))
      for (const grade of GRADES) {
        legendItems
          .get(grade)
          ?.replaceChildren(
            createElement('span', 'ev-gauge__swatch'),
            createElement('span', 'ev-gauge__oddsText', t('envelope.odds', { grade: t(gradeNameKey(grade)), odds: gradeOddsLabel(grade, table) })),
          )
      }
    },

    resetNeedle(): void {
      needle.classList.remove('is-instant')
      needle.style.setProperty('--ev-needle', '0')
    },

    setNeedle(gradeRoll: number, instant: boolean): void {
      // 바늘의 목적지는 rng가 뽑은 실수 그 값이다. 여기서 값을 만지면 화면이 확률표를 거짓으로 말한다.
      if (instant) needle.classList.add('is-instant')
      needle.style.setProperty('--ev-needle', gradeRoll.toFixed(4))
    },

    armReel(table: EnvelopeTable, gradeRoll: number, seq: number): void {
      run = reelRunFor(gradeRoll, seq)
      reelTrack.replaceChildren()
      for (let copy = 0; copy < run.copies; copy += 1) {
        const unit = createElement('span', 'ev-reel__copy')
        appendBand(unit, table, 'ev-reel__seg')
        reelTrack.append(unit)
      }
      gauge.style.setProperty('--ev-reel-copies', String(run.copies))
      // 길이 0으로 되돌린다. `is-instant`(탭·움직임 줄이기)와 달리 다음 회차에서 다시 켜져야 하는 값이다.
      gauge.style.setProperty('--ev-reel-dur', '0ms')
      reelTrack.classList.remove('is-instant')
      reelSpin.classList.remove('is-spinning')
      reel.classList.remove('is-landed')
      leadPeriodMs = 0
      placeTrack(run.from)
    },

    leadReel(periodMs: number): void {
      if (!(periodMs > 0)) return
      leadPeriodMs = periodMs
      reelSpin.style.setProperty('--ev-reel-period', `${periodMs.toFixed(2)}ms`)
      reelSpin.classList.add('is-spinning')
    },

    spinReel(durationMs: number): void {
      /*
       * **제자리 회전이 남긴 위상을 이어받는다.** 리드는 명목상 정수 바퀴에서 끝나지만(`reelSpinPeriodMs`)
       * 실제 길이는 카드 아홉 장을 넘긴 타이머의 실시간이라 몇 ms 길다. 그 잔여를 무시하고 애니메이션을
       * 걷으면 트랙이 그만큼 왼쪽으로 튄다(측정 0.25 사본 ≈ 44px). 자리를 지어내는 것이 아니라 **지금
       * 보이는 자리**를 그대로 옮겨 적는 것이고, 착지 자리는 절대값이라 하나도 움직이지 않는다.
       */
      const carry = spinCarry()
      reelSpin.classList.remove('is-spinning')
      reelTrack.classList.remove('is-instant')
      if (carry > 0) {
        // 전환 없이 그 자리에 앉힌 뒤 레이아웃을 한 번 굳힌다 - 안 그러면 두 값이 한 프레임에 합쳐져
        // 전환의 출발점이 옛 자리가 되고, 이어 붙이려던 위상이 그대로 점프로 돌아온다.
        reelTrack.classList.add('is-instant')
        placeTrack(run.from + carry)
        void reelTrack.offsetWidth
        reelTrack.classList.remove('is-instant')
      }
      gauge.style.setProperty('--ev-reel-dur', `${durationMs}ms`)
      placeTrack(run.to)
    },

    landReel(): void {
      reel.classList.add('is-landed')
    },

    settleReel(): void {
      // 컷에는 표식이 없다. 탭 건너뛰기·움직임 줄이기에서 "움직이는 것은 하나도 없다"가 이 화면의 약속이다.
      reel.classList.remove('is-landed')
      reelSpin.classList.remove('is-spinning')
      reelTrack.classList.add('is-instant')
      placeTrack(run.to)
    },

    paintPity(value: Pity | null, table: EnvelopeTable): void {
      pity.hidden = value === null
      if (value === null) return
      const remaining = pityRemaining(value, table)
      const limits = table.pity
      setText(pityRare.label, remaining.rare <= 1 ? t('envelope.pity.rareNow') : t('envelope.pity.rare', { count: remaining.rare }))
      setBarRatio(pityRare.fill, value.sinceRare / limits.rareAfter)
      setText(
        pityLegend.label,
        remaining.legend <= 1 ? t('envelope.pity.legendNow') : t('envelope.pity.legend', { count: remaining.legend }),
      )
      setBarRatio(pityLegend.fill, value.sinceLegend / limits.legendAfter)
    },
  }
}
